/**
 * saveSettingsConcurrently
 *
 * The BFF's UpdateSettings endpoint processes settings synchronously through
 * ECP, averaging ~5s per setting (observed range: 1–7s). When a batch of N
 * settings is sent in a single POST, the user waits N × ~5s with no feedback
 * (e.g. 42 settings ≈ 3.5 min).
 *
 * This utility breaks the batch into individual POST calls and runs them through
 * a concurrency-limited pool (default 5). Each setting resolves independently,
 * so the UI can show real-time per-setting progress (pending → completed/failed)
 * while keeping server load bounded. Failures are isolated — one setting failing
 * does not block or cancel the others.
 *
 * With concurrency of 5, a 42-setting batch completes in ~44s wall time
 * instead of ~210s sequential.
 */
// TODO: Generalize beyond DesiredSettingValue for non-settings save flows.
// The concurrency pool logic is generic — only the type parameter and
// .settingId access need to change. See settings-save.types.ts.
import type { DesiredSettingValue } from '@crossnokaye/typescript-sdk/models/components';

/** Default concurrency limit for parallel save requests. */
export const CONCURRENT_SAVE_LIMIT = 8;

interface SaveSettingsConcurrentlyOptions {
  settings: DesiredSettingValue[];
  updateFn: (settings: DesiredSettingValue[]) => Promise<unknown>;
  concurrency?: number;
  signal?: AbortSignal;
}

interface SaveSettingsConcurrentlyResult {
  completed: string[];
  failed: string[];
  aborted: boolean;
}

export async function saveSettingsConcurrently({
  settings,
  updateFn,
  concurrency = CONCURRENT_SAVE_LIMIT,
  signal,
}: SaveSettingsConcurrentlyOptions): Promise<SaveSettingsConcurrentlyResult> {
  const completed: string[] = [];
  const failed: string[] = [];
  let aborted = false;

  if (settings.length === 0) {
    return { completed, failed, aborted };
  }

  // Shared index counter — each worker increments atomically (single-threaded JS)
  // to pull the next unprocessed setting from the array.
  let nextIndex = 0;

  const processNext = async (): Promise<void> => {
    while (nextIndex < settings.length) {
      if (signal?.aborted) {
        aborted = true;
        return;
      }

      const index = nextIndex++;
      const setting = settings[index];
      const settingId = setting.settingId;

      try {
        // Wrap each setting in an array because updateFn (useUpdateSettings)
        // expects DesiredSettingValue[] — we send one at a time for granular progress.
        await updateFn([setting]);
        completed.push(settingId);
      } catch {
        failed.push(settingId);
      }
    }
  };

  // Spawn N workers that share the work queue. Each worker runs processNext()
  // which loops until all settings are claimed. This caps in-flight requests
  // to `concurrency` without needing an external semaphore library.
  const workers = Array.from({ length: Math.min(Math.max(concurrency, 1), settings.length) }, () =>
    processNext()
  );

  await Promise.all(workers);

  return { completed, failed, aborted };
}
